/** Closed maintained event-rule table. This is deliberately data matching, not
 * an expression language: one reviewed trigger, target, effect and profile. */
export interface EventRuleV1{on:'collected';target:'player';effect:'speed-boost';profile:'short-boost@1'}
export interface EventRuleTriggerV1{on:'collected';target:'player';tick:number}
export interface EventRuleStateV1{
  active:boolean;profile:null|'short-boost@1';multiplier:number;remainingTicks:number;
  activeFromTick:number|null;activeUntilTick:number|null;firings:number;
}
export type EventRuleTransitionV1={
  kind:'effect-scheduled'|'effect-started'|'effect-expired';tick:number;
  data:{ruleId:'collected/player/speed-boost/short-boost@1';profile:'short-boost@1';multiplier:1.1;durationTicks:4;activeFromTick:number;activeUntilTick:number};
};
export const SHORT_BOOST_RULE_V1:Readonly<EventRuleV1>=Object.freeze({on:'collected',target:'player',effect:'speed-boost',profile:'short-boost@1'});
export const SHORT_BOOST_EFFECT_PROFILE_V1=Object.freeze({multiplier:1.1 as const,durationTicks:4 as const});
const RULE_ID='collected/player/speed-boost/short-boost@1' as const;
const{multiplier:MULTIPLIER,durationTicks:DURATION_TICKS}=SHORT_BOOST_EFFECT_PROFILE_V1;

function record(value:unknown,keys:readonly string[]):asserts value is Record<string,unknown>{
  if(!value||typeof value!=='object'||Array.isArray(value)||![Object.prototype,null].includes(Object.getPrototypeOf(value))
    ||Reflect.ownKeys(value).length!==keys.length||Object.keys(value).sort().join(',')!==[...keys].sort().join(',')
    ||Object.values(Object.getOwnPropertyDescriptors(value)).some(descriptor=>!('value'in descriptor)))throw new RangeError('Invalid event-rule record');
}
function tick(value:unknown):asserts value is number{if(typeof value!=='number'||!Number.isSafeInteger(value)||value<0)throw new RangeError('Invalid event-rule tick');}
export function validateEventRulesV1(input:unknown):[EventRuleV1]{
  if(!Array.isArray(input)||input.length!==1)throw new RangeError('Event-rule V1 requires exactly one reviewed rule');
  const descriptors=Object.getOwnPropertyDescriptors(input);
  if(Reflect.ownKeys(input).length!==2||!descriptors[0]||!('value'in descriptors[0]))throw new RangeError('Invalid event-rule table');
  record(input[0],['on','target','effect','profile']);const rule=input[0];
  if(rule.on!=='collected'||rule.target!=='player'||rule.effect!=='speed-boost'||rule.profile!=='short-boost@1')throw new RangeError('Unsupported event rule');
  return[structuredClone(SHORT_BOOST_RULE_V1)];
}

export function createEventRuleDispatcherV1(input:unknown){
  validateEventRulesV1(input);
  let activeFrom:number|null=null,activeUntil:number|null=null,lastFireTick:number|null=null,lastBeginTick=-1,firings=0;
  const transition=(kind:EventRuleTransitionV1['kind'],at:number):EventRuleTransitionV1=>({kind,tick:at,data:{ruleId:RULE_ID,profile:'short-boost@1',multiplier:MULTIPLIER,
    durationTicks:DURATION_TICKS,activeFromTick:activeFrom!,activeUntilTick:activeUntil!}});
  const snapshot=(at:number):EventRuleStateV1=>{
    tick(at);const active=activeFrom!==null&&activeUntil!==null&&at>=activeFrom&&at<=activeUntil;
    return{active,profile:active?'short-boost@1':null,multiplier:active?MULTIPLIER:1,remainingTicks:active?activeUntil!-at+1:0,
      activeFromTick:active?activeFrom:null,activeUntilTick:active?activeUntil:null,firings};
  };
  return{
    beginTick(at:number):EventRuleTransitionV1[]{
      tick(at);if(lastBeginTick>=0&&at!==lastBeginTick+1)throw new RangeError('Event-rule ticks must increase exactly once');lastBeginTick=at;
      if(activeUntil!==null&&at===activeUntil+1){const expired=transition('effect-expired',at);activeFrom=null;activeUntil=null;return[expired];}
      if(activeFrom===at)return[transition('effect-started',at)];return[];
    },
    dispatch(trigger:unknown):EventRuleTransitionV1[]{
      record(trigger,['on','target','tick']);tick(trigger.tick);
      if(trigger.on!=='collected'||trigger.target!=='player')throw new RangeError('Unsupported event-rule trigger');
      if(trigger.tick!==lastBeginTick)throw new RangeError('Event-rule trigger is outside current tick');
      if(lastFireTick===trigger.tick)return[];
      lastFireTick=trigger.tick;firings++;
      if(activeFrom===null||activeUntil===null||trigger.tick>activeUntil){activeFrom=trigger.tick+1;activeUntil=trigger.tick+DURATION_TICKS;}
      else activeUntil=Math.max(activeUntil,trigger.tick+DURATION_TICKS);
      return[transition('effect-scheduled',trigger.tick)];
    },
    snapshot,
    reset():void{activeFrom=null;activeUntil=null;lastFireTick=null;lastBeginTick=-1;firings=0;},
  };
}
